import { z } from "zod";
import { isWordType, type WordType } from "../../../domain/vocabulary/types.ts";
import {
  AI_CONTRACT_VERSION,
  discoverMeaningsResultSchema,
  type DiscoverMeaningsRequest,
  type DiscoverMeaningsResult,
  type MeaningCandidate,
} from "../../../contracts/ai/schemas.ts";
import { ProductError } from "../../../contracts/errors.ts";

export const DISCOVER_CANDIDATE_LIMIT = 8;

export const discoverModelSchema = z.object({
  candidates: z.array(
    z.object({
      id: z.string().optional().default(""),
      type: z.string().optional().default("other"),
      shortLabel: z.string().describe("A short label in the simplest everyday English."),
      definition: z.string().describe("One short definition in the simplest everyday English."),
      example: z.string().describe("One short example sentence in the simplest everyday English."),
      matchKind: z.string().optional().default("new"),
      existingMeaningId: z.string().nullable().optional().default(null),
      uncertainReason: z.string().nullable().optional().default(null),
      contextMatch: z.boolean().optional().default(false),
    }),
  ),
  corrections: z.array(z.string()).optional().default([]),
  moreAvailable: z.boolean().optional().default(false),
});

export type DiscoverModel = z.infer<typeof discoverModelSchema>;

const TYPE_ALIASES: Record<string, WordType> = {
  phrasalverb: "phrasal_verb",
  phrasalverbs: "phrasal_verb",
  verbphrase: "phrasal_verb",
  multiwordverb: "phrasal_verb",
  particleverb: "phrasal_verb",
  prepositionalverb: "phrasal_verb",
  nounphrase: "phrase",
  adj: "adjective",
  adv: "adverb",
};

export function normalizeWordType(value: string): WordType {
  const underscored = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (isWordType(underscored)) {
    return underscored;
  }
  const compact = underscored.replace(/_/g, "");
  return TYPE_ALIASES[compact] ?? TYPE_ALIASES[underscored] ?? "other";
}

function clip(value: string, max: number): string {
  const trimmed = value.trim().replace(/\s+/g, " ");
  if (trimmed.length <= max) {
    return trimmed;
  }
  return trimmed.slice(0, max).trim();
}

function uniqueId(base: string, used: Set<string>): string {
  let candidate = base;
  let suffix = 2;
  while (used.has(candidate)) {
    candidate = `${base}-${suffix}`;
    suffix += 1;
  }
  used.add(candidate);
  return candidate;
}

function mapMatch(candidate: DiscoverModel["candidates"][number]): MeaningCandidate["match"] {
  const existingId = candidate.existingMeaningId?.trim();
  const kind = candidate.matchKind.trim().toLowerCase();
  if (kind === "new" || !existingId) {
    return { kind: "new" };
  }
  if (kind === "saved") {
    return { kind: "saved", existingMeaningId: existingId };
  }
  return {
    kind: "uncertain",
    existingMeaningId: existingId,
    reason:
      clip(candidate.uncertainReason ?? "Possible overlap with a saved meaning.", 200) ||
      "Possible overlap with a saved meaning.",
  };
}

export function mapDiscoverResult(
  raw: DiscoverModel,
  request: Pick<DiscoverMeaningsRequest, "requestId" | "normalizedWord">,
): DiscoverMeaningsResult {
  const usedIds = new Set<string>();
  const mapped: MeaningCandidate[] = [];
  for (const [index, candidate] of raw.candidates.entries()) {
    const shortLabel = clip(candidate.shortLabel, 120);
    const definition = clip(candidate.definition, 400);
    const example = clip(candidate.example, 280);
    if (!shortLabel || !definition || !example) {
      continue;
    }
    const fallbackId = `cand-${request.normalizedWord.replace(/\s+/g, "-")}-${index + 1}`;
    mapped.push({
      id: uniqueId(clip(candidate.id, 80) || fallbackId, usedIds),
      type: normalizeWordType(candidate.type),
      shortLabel,
      definition,
      example,
      contextMatch: Boolean(candidate.contextMatch),
      match: mapMatch(candidate),
    });
  }
  const overflow = mapped.length > DISCOVER_CANDIDATE_LIMIT;
  const parsed = discoverMeaningsResultSchema.safeParse({
    contractVersion: AI_CONTRACT_VERSION,
    requestId: request.requestId,
    moreAvailable: raw.moreAvailable || overflow,
    corrections: raw.corrections.map((item) => clip(item, 200)).filter(Boolean),
    candidates: mapped.slice(0, DISCOVER_CANDIDATE_LIMIT),
  });
  if (!parsed.success || parsed.data.candidates.length === 0) {
    throw new ProductError("invalid_response", "The model returned an invalid meaning list.");
  }
  return parsed.data;
}
